import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { InputEdit, InputMui } from '../../../components';
import ArrowButton from '../../../components/arrow-button';
import { useBackdropContext } from '../../../context/backdrop';
import { ChangeUserNameMutation, ChangeUserNameMutationVariables } from '../../../data-access/gql-types/graphql';
import { CHANGE_NAME } from '../../../data-access/mutations/user';
import { useAuth } from '../../../hooks';
import * as storage from '../../../integrations/lavva/storage';
import { ROUTES } from '../../../routes/routes';
import { useUserProfileStore } from '../../../store/user-profile';
import { toastError, toastSuccess } from '../../../utils/toast';
import { patterns } from '../../../utils/validation';

const SettingsForm: React.FC = () => {
  const navigate = useNavigate();
  const { t: tc } = useTranslation('common');
  const { t, i18n } = useTranslation('profile');
  const { keycloak } = useAuth();
  const [disabled, setDisabled] = useState<boolean>(true);
  const { control, handleSubmit, setValue } = useForm({ defaultValues: { firstName: '' } });
  const {
    meData: { firstName, email },
    refetchUserMe,
  } = useUserProfileStore((s) => s);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const [changeUserName] = useMutation<ChangeUserNameMutation, ChangeUserNameMutationVariables>(CHANGE_NAME);

  useEffect(() => setValue('firstName', firstName), [firstName]);

  const onSubmit = ({ firstName }) => {
    turnOnBackdrop();
    setDisabled(true);
    changeUserName({
      variables: { input: { firstName } },
      onCompleted: (data) => {
        turnOffBackdrop();
        if (data.changeUserName.result?.ok) {
          refetchUserMe();
          toastSuccess({ content: t('toastNameSuccess') });
        } else {
          if (data.changeUserName.errors) {
            toastError({ content: tc('errors.somethingWentWrong') });
          }
        }
      },
      onError: () => {
        turnOffBackdrop();
        toastError({ content: t('toastError') });
      },
    });
  };

  const handleChangePassword = () => {
    const theme = storage.getItem('theme');
    keycloak.login({ action: 'UPDATE_PASSWORD', locale: i18n.language, theme });
  };

  return (
    <>
      <div className="grid-list-24">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: tc('isRequired') as string,
              maxLength: {
                value: 40,
                message: tc('tooLongName'),
              },
              pattern: {
                value: patterns.name,
                message: tc('invalidName'),
              },
            }}
            render={({ field, fieldState }) => (
              <InputEdit
                disabled={disabled}
                setDisabled={setDisabled}
                label={t('username')}
                onChange={field.onChange}
                value={field.value}
                {...(fieldState.error && { errorMessage: fieldState.error.message })}
              />
            )}
          />
        </form>
        <InputMui type="text" value={email} label={t('email')} id="email" disabled readOnly />
        <ArrowButton
          label={t('password')}
          title={t('change-password')}
          onClick={handleChangePassword}
          className="m-b-24"
          small
        />

        <ArrowButton
          label={t('credentials.signingIn')}
          title={t('credentials.configureOptions')}
          onClick={() => navigate(ROUTES.Credentials())}
          className="m-b-24"
          small
        />
      </div>
    </>
  );
};

export default SettingsForm;
